import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Stethoscope, FileText, Users, BookOpen, HandshakeIcon, Download, Mail } from "lucide-react";

export default function ForPhysiotherapists() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="physio-portal-page">
      {/* HERO */}
      <section className="section-dark relative overflow-hidden py-24 sm:py-32">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(52,216,160,0.12),transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-overline text-[#34D8A0]">
            <Stethoscope className="h-3.5 w-3.5" /> For Physiotherapists
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 max-w-3xl font-display text-5xl font-black leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          >
            Nutrition that<br />
            <span className="text-[#34D8A0]">complements rehabilitation.</span>
          </motion.h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
            Practical nutritional support designed for physiotherapy-led recovery pathways. Explore condition-based guides, protocol resources and clinic partner options.
          </p>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: BookOpen, title: "Clinical Guides", desc: "Downloadable condition and product guides — coming soon.", cta: "Preview coming soon" },
            { icon: FileText, title: "Condition → Formula Guide", desc: "Interactive matrix mapping conditions to Physio Essential formulas.", cta: "View matrix" },
            { icon: Users, title: "Patient Education", desc: "Printable and digital resources for your patient consultations.", cta: "Browse resources" },
            { icon: Stethoscope, title: "Recovery Protocols", desc: "Phased clinical pathways: knee OA, ACL, joint replacement, sports, geriatric mobility.", cta: "Open protocols" },
            { icon: Download, title: "Product Education", desc: "Ingredient and formulation information for your clinical review.", cta: "Download product sheets" },
            { icon: HandshakeIcon, title: "Clinic Partner Programme", desc: "Register your clinic for professional pricing and clinical support.", cta: "Become a partner" },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group border border-black/10 bg-white p-7 transition-colors hover:border-[#2F80ED]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#2F80ED]/30 bg-[#2F80ED]/10">
                  <Icon className="h-5 w-5 text-[#2F80ED]" strokeWidth={1.6} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-[#0A2540]">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/60">{c.desc}</p>
                <div className="mt-6 font-mono text-[11px] uppercase tracking-overline text-[#2F80ED]">{c.cta} →</div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA STRIP */}
        <div className="mt-16 rounded-2xl border border-black/10 bg-[#F9F9F6] p-10 text-center">
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Clinical Team Contact</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Request product training or a clinic partnership call.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-black/60">
            Our clinical team can walk your team through the range, ingredient rationale and how each formula complements rehabilitation programmes.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-8 py-4 font-semibold text-white hover:bg-[#2F80ED]"
              data-testid="physio-contact-btn"
            >
              <Mail className="h-4 w-4" /> Contact Clinical Team
            </Link>
            <Link
              to="/find-my-formula"
              className="inline-flex items-center gap-2 rounded-full border border-black/20 px-8 py-4 font-semibold text-[#0A2540] hover:bg-black hover:text-white"
            >
              Find My Recovery Formula <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
